import { AdminCouponResponse, Coupon, Organisation } from '@fsp/types';
import LocalOfferTwoTone from '@mui/icons-material/LocalOfferTwoTone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BreadCrumb from '../../../components/BreadCrumb';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import OrganisationChip from '../../../components/OrganisationChip';
import { apiClient } from '../../../services/apiClient';
import { formatDate, DateFormatOptions } from '@fsp/shared';
import { getSubscriptionStatusColor } from '../../../utils/getSubscriptionStatusColor';

export default function CouponView() {
  const { couponId } = useParams<{ couponId: string }>();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [selectedPromoCode, setSelectedPromoCode] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newPromoCode, setNewPromoCode] = useState('');
  const [addingPromoCode, setAddingPromoCode] = useState(false);
  const [deletePromoCode, setDeletePromoCode] = useState<string | null>(null);
  const [reactivatePromoCode, setReactivatePromoCode] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (couponId) {
      fetchCoupon();
    }
  }, [couponId]);

  const fetchCoupon = async () => {
    if (!couponId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/admin/coupons/${couponId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch coupon details');
      }

      const data: AdminCouponResponse = await response.json();
      setCoupon(data.coupon);
      setOrganisations(data.organisations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching coupon:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async (code: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handleDelete = async () => {
    if (!couponId) return;
    setDeleting(true);
    try {
      const response = await apiClient.delete(`/admin/coupons/${couponId}`);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete coupon');
      }
      enqueueSnackbar('Coupon deleted successfully', { variant: 'success' });
      navigate('/app/coupons');
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to delete coupon', {
        variant: 'error',
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleAddPromoCode = async () => {
    if (!couponId || !newPromoCode.trim()) return;
    setAddingPromoCode(true);
    try {
      const response = await apiClient.post(`/admin/coupons/${couponId}/promotion-codes`, {
        code: newPromoCode.trim(),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to add promotion code');
      }
      enqueueSnackbar('Promotion code added', { variant: 'success' });
      setNewPromoCode('');
      fetchCoupon();
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to add promotion code', {
        variant: 'error',
      });
    } finally {
      setAddingPromoCode(false);
    }
  };

  const handleDeletePromoCode = async () => {
    if (!couponId || !deletePromoCode) return;
    try {
      const response = await apiClient.delete(
        `/admin/coupons/${couponId}/promotion-codes/${deletePromoCode}`
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to deactivate promotion code');
      }
      enqueueSnackbar('Promotion code deactivated', { variant: 'success' });
      fetchCoupon();
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to deactivate promotion code', {
        variant: 'error',
      });
    } finally {
      setDeletePromoCode(null);
    }
  };

  const handleReactivatePromoCode = async () => {
    if (!couponId || !reactivatePromoCode) return;
    try {
      const response = await apiClient.post(
        `/admin/coupons/${couponId}/promotion-codes/${reactivatePromoCode}/reactivate`
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to reactivate promotion code');
      }
      enqueueSnackbar('Promotion code reactivated', { variant: 'success' });
      fetchCoupon();
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to reactivate promotion code', {
        variant: 'error',
      });
    } finally {
      setReactivatePromoCode(null);
    }
  };

  const filteredOrganisations = useMemo(() => {
    if (selectedPromoCode === 'all') {
      return organisations;
    }
    return organisations.filter((org) => org.promotionCode === selectedPromoCode);
  }, [organisations, selectedPromoCode]);

  const InfoItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  const formatDiscount = (coupon: Coupon) => {
    if (coupon.percentOff) {
      return `${coupon.percentOff}% off`;
    }
    if (coupon.amountOff && coupon.currency) {
      return `${coupon.amountOff.toFixed(2)} ${coupon.currency.toUpperCase()} off`;
    }
    return 'N/A';
  };

  const formatDuration = (coupon: Coupon) => {
    if (coupon.duration === 'forever') {
      return 'Forever';
    }
    if (coupon.duration === 'once') {
      return 'Once';
    }
    if (coupon.duration === 'repeating' && coupon.durationInMonths) {
      return `${coupon.durationInMonths} month${coupon.durationInMonths > 1 ? 's' : ''}`;
    }
    return coupon.duration;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !coupon) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <BreadCrumb />
          <Typography variant="h5" color="error">
            {error || 'Coupon not found'}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="xl">
        <BreadCrumb customLabel={coupon.name || coupon.id} />

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'success.main' }}>
              <LocalOfferTwoTone sx={{ fontSize: 32, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {coupon.name || coupon.id}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {coupon.id}
                </Typography>
                <Link
                  href={`https://dashboard.stripe.com/coupons/${coupon.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
                >
                  View in Stripe
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Link>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={coupon.valid ? 'Valid' : 'Invalid'}
              color={coupon.valid ? 'success' : 'default'}
              sx={{ height: 32, fontSize: '0.875rem' }}
            />
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={deleting}
            >
              Delete
            </Button>
          </Box>
        </Box>

        {/* Promotion Codes Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Promotion Codes
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {coupon.promotionCodes.map((promoCode, idx) => (
                <Chip
                  key={idx}
                  label={
                    copiedCode === promoCode.code
                      ? 'Copied!'
                      : `${promoCode.code} (${promoCode.attributions})`
                  }
                  color={
                    !promoCode.active
                      ? 'default'
                      : copiedCode === promoCode.code
                        ? 'success'
                        : 'primary'
                  }
                  variant={promoCode.active ? 'filled' : 'outlined'}
                  onClick={promoCode.active ? (e) => handleCopyCode(promoCode.code, e) : undefined}
                  onDelete={
                    promoCode.active
                      ? () => setDeletePromoCode(promoCode.code)
                      : () => setReactivatePromoCode(promoCode.code)
                  }
                  deleteIcon={promoCode.active ? undefined : <RestoreIcon />}
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                    height: 36,
                    ...(promoCode.active && { cursor: 'pointer' }),
                  }}
                />
              ))}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  size="small"
                  placeholder="NEW_CODE"
                  value={newPromoCode}
                  onChange={(e) => setNewPromoCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newPromoCode.trim()) handleAddPromoCode();
                  }}
                  disabled={addingPromoCode}
                  sx={{ width: 140, '& input': { fontFamily: 'monospace' } }}
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleAddPromoCode}
                  disabled={addingPromoCode || !newPromoCode.trim()}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Click to copy - Numbers in parentheses show attribution count
            </Typography>
          </CardContent>
        </Card>

        {/* Coupon Details Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Coupon Details
            </Typography>
            <Grid container spacing={4}>
              {/* Discount Information */}
              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
                >
                  Discount
                </Typography>
                <Stack spacing={2}>
                  <InfoItem label="Discount Value" value={formatDiscount(coupon)} />
                  <InfoItem label="Duration" value={formatDuration(coupon)} />
                  {coupon.redeemBy && (
                    <InfoItem
                      label="Redeem By"
                      value={formatDate(coupon.redeemBy, DateFormatOptions.DATEONLY)}
                    />
                  )}
                </Stack>
              </Grid>

              {/* Usage Statistics */}
              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
                >
                  Usage
                </Typography>
                <Stack spacing={2}>
                  <InfoItem label="Times Redeemed" value={coupon.timesRedeemed} />
                  <InfoItem label="Max Redemptions" value={coupon.maxRedemptions || 'Unlimited'} />
                  <InfoItem
                    label="Remaining"
                    value={
                      coupon.maxRedemptions
                        ? coupon.maxRedemptions - coupon.timesRedeemed
                        : 'Unlimited'
                    }
                  />
                </Stack>
              </Grid>

              {/* Metadata */}
              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
                >
                  Metadata
                </Typography>
                <Stack spacing={2}>
                  <InfoItem
                    label="Created"
                    value={formatDate(coupon.created, DateFormatOptions.DATEANDTIME)}
                  />
                  <InfoItem
                    label="Status"
                    value={
                      <Chip
                        label={coupon.valid ? 'Valid' : 'Invalid'}
                        color={coupon.valid ? 'success' : 'default'}
                        size="small"
                      />
                    }
                  />
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Organisations Using This Coupon */}
        <Card>
          <CardContent>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Organisations ({filteredOrganisations.length}
                {selectedPromoCode !== 'all' && ` of ${organisations.length}`})
              </Typography>
              {coupon && coupon.promotionCodes.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter by Promo Code</InputLabel>
                  <Select
                    value={selectedPromoCode}
                    label="Filter by Promo Code"
                    onChange={(e) => setSelectedPromoCode(e.target.value)}
                  >
                    <MenuItem value="all">All Codes</MenuItem>
                    {coupon.promotionCodes.map((promoCode) => (
                      <MenuItem key={promoCode.code} value={promoCode.code}>
                        {promoCode.code} ({promoCode.attributions})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            {organisations.length === 0 ? (
              <Typography color="text.secondary">
                No organisations have redeemed this coupon yet
              </Typography>
            ) : filteredOrganisations.length === 0 ? (
              <Typography color="text.secondary">
                No organisations found for the selected promotion code
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>ABN</TableCell>
                      <TableCell>Promotion Code Used</TableCell>
                      <TableCell>Subscription Status</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrganisations.map((org) => (
                      <TableRow
                        key={org.id}
                        onClick={() => navigate(`/app/organisations/${org.id}`)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            cursor: 'pointer',
                          },
                        }}
                      >
                        <TableCell>
                          <OrganisationChip organisation={org} clickable={false} />
                        </TableCell>
                        <TableCell>{org.abn || '-'}</TableCell>
                        <TableCell>
                          {org.promotionCode ? (
                            <Chip
                              label={org.promotionCode}
                              color="primary"
                              size="small"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={org.subscriptionStatus}
                            color={getSubscriptionStatusColor(org.subscriptionStatus)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(org.createdAt, DateFormatOptions.DATEONLY)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Container>

      <ConfirmDialog
        open={!!deletePromoCode}
        title="Deactivate Promotion Code"
        message={`Are you sure you want to deactivate the promotion code "${deletePromoCode}"? It will no longer be usable by customers.`}
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={handleDeletePromoCode}
        onCancel={() => setDeletePromoCode(null)}
      />

      <ConfirmDialog
        open={!!reactivatePromoCode}
        title="Reactivate Promotion Code"
        message={`Are you sure you want to reactivate the promotion code "${reactivatePromoCode}"? It will become usable by customers again.`}
        confirmLabel="Reactivate"
        confirmColor="success"
        onConfirm={handleReactivatePromoCode}
        onCancel={() => setReactivatePromoCode(null)}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Coupon"
        message={`Are you sure you want to delete the coupon "${coupon.name || coupon.id}"? This will also delete all associated promotion codes. This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </>
  );
}
